import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Loader2, PenTool, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import SignatureCanvas from 'react-signature-canvas';
import { Turnstile } from '@marsidev/react-turnstile';
import axios from 'axios';
import ClientPicker from '../components/ClientPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Terms & Conditions Text
const TERMS_TEXT = `ALLGEMEINE GESCHÄFTSBEDINGUNGEN FÜR SHOOTING EINVERSTÄNDNISERKLÄRUNG

§1 VERBINDLICHE ZUSAGE
Mit der Unterzeichnung dieser Vereinbarung bestätige ich meine verbindliche Teilnahme am angegebenen Shooting-Termin. Diese Zusage ist rechtlich bindend.

§2 ERSCHEINUNGSPFLICHT
Ich verpflichte mich, pünktlich und vollständig vorbereitet am vereinbarten Shooting-Ort zu erscheinen. Bei Verhinderung habe ich dies unverzüglich, spätestens jedoch 48 Stunden vor dem Termin, mitzuteilen.

§3 ABSAGE UND NICHTERSCHEINEN
Bei unentschuldigtem Nichterscheinen oder kurzfristiger Absage (weniger als 48 Stunden vor dem Termin) behalte ich mir vor, dem Teilnehmer einen Strafpunkt zu vergeben. Bei Erreichen von 3 Strafpunkten erfolgt der Ausschluss aus dem Programm.

§4 BILDRECHTE
Ich erteile hiermit meine ausdrückliche Zustimmung zur Herstellung von Bild- und Videoaufnahmen meiner Person durch die The Guardians of Social Media GmbH und/oder deren beauftragte Dritte.

Die erstellten Aufnahmen dürfen zeitlich, inhaltlich und räumlich unbeschränkt – insbesondere aber nicht nur zu Werbezwecken – in Medien jeglicher Form veröffentlicht, vervielfältigt, verbreitet und verwertet sowie digitalisiert, elektronisch gespeichert und verändert werden.

§5 VERGÜTUNG
Die Vergütung für das Shooting wird separat vereinbart und entspricht den im Vorfeld besprochenen Konditionen.

§6 VERTRAULICHKEIT
Alle im Rahmen des Shootings erhaltenen Informationen über den Kunden, dessen Produkte oder Dienstleistungen sind vertraulich zu behandeln.

§7 SALVATORISCHE KLAUSEL
Sollte eine Bestimmung dieser Vereinbarung unwirksam sein oder werden, so wird die Wirksamkeit der übrigen Bestimmungen hiervon nicht berührt.

§8 DATENSCHUTZ
Die erhobenen personenbezogenen Daten werden ausschließlich zum Zweck der Durchführung des Shootings und der Verwaltung des Programms verwendet. Eine Weitergabe an Dritte erfolgt nur im Rahmen der vereinbarten Nutzung der Bildrechte.

Mit meiner Unterschrift bestätige ich, dass ich diese Bedingungen gelesen und verstanden habe und ihnen zustimme.`;

const ShootingEinverstaendnisPage = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [direction, setDirection] = useState(0);
    const [turnstileToken, setTurnstileToken] = useState(null);
    
    const signatureRef = useRef(null);
    
    // Form data - "Firma" instead of "client_name"
    const [formData, setFormData] = useState({
        company_name: '',  // "Firma" field
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        shooting_date: null,
        consent_accepted: false,
        signature_data: ''
    });

    const totalSlides = 5;

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const clearSignature = () => {
        signatureRef.current?.clear();
        updateField('signature_data', '');
    };

    const saveSignature = () => {
        if (signatureRef.current?.isEmpty()) {
            updateField('signature_data', '');
        } else {
            const data = signatureRef.current?.toDataURL('image/png');
            updateField('signature_data', data);
        }
    };

    const canProceed = () => {
        switch (currentSlide) {
            case 0: return !!formData.company_name.trim();
            case 1: return formData.first_name && formData.last_name && formData.email && formData.phone;
            case 2: return !!formData.shooting_date;
            case 3: return formData.consent_accepted && (!TURNSTILE_SITE_KEY || turnstileToken);
            case 4: return !!formData.signature_data;
            default: return true;
        }
    };

    const handleSubmit = async () => {
        if (!formData.signature_data) {
            toast.error('Bitte unterschreibe das Formular');
            return;
        }

        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            toast.error('Bitte bestätige, dass du kein Roboter bist');
            return;
        }

        setIsSubmitting(true);

        try {
            const submitData = {
                ...formData,
                shooting_date: formData.shooting_date ? format(formData.shooting_date, 'yyyy-MM-dd') : null,
                turnstile_token: turnstileToken
            };

            const res = await axios.post(`${API_URL}/api/shooting-einverstaendnis/submit`, submitData);
            
            if (res.data.success) {
                setShowSuccess(true);
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error(err.response?.data?.detail || 'Fehler beim Senden');
        } finally {
            setIsSubmitting(false);
        }
    };

    const slideVariants = {
        enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (direction) => ({ x: direction < 0 ? 300 : -300, opacity: 0 })
    };

    const goNext = () => {
        if (canProceed() && currentSlide < totalSlides - 1) {
            setDirection(1);
            setCurrentSlide(currentSlide + 1);
        }
    };

    const goPrev = () => {
        if (currentSlide > 0) {
            setDirection(-1);
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Success Screen
    if (showSuccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-white/20"
                    >
                        {/* Logo */}
                        <img 
                            src="/tgosm-logo.png" 
                            alt="TGOSM" 
                            className="w-16 h-16 mx-auto mb-4 object-contain"
                        />
                        
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
                        >
                            <Check className="w-10 h-10 text-white" />
                        </motion.div>
                        
                        <h1 className="text-2xl font-bold text-white mb-4">
                            Vielen Dank für dein Einverständnis!
                        </h1>
                        
                        <p className="text-white/70 mb-6">
                            Deine Einverständniserklärung wurde erfolgreich übermittelt. Wir freuen uns auf dich!
                        </p>
                        
                        <div className="p-4 bg-white/5 rounded-xl text-sm text-white/60">
                            <p><strong>Firma:</strong> {formData.company_name}</p>
                            <p><strong>Datum:</strong> {formData.shooting_date ? format(formData.shooting_date, 'dd. MMMM yyyy', { locale: de }) : '-'}</p>
                        </div>
                    </motion.div>
                </div>
                
                {/* Footer */}
                <footer className="py-6 text-center text-white/40 text-sm">
                    <p>© {new Date().getFullYear()} The Guardians of Social Media GmbH. Alle Rechte vorbehalten.</p>
                </footer>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4 py-8">
                <div className="w-full max-w-lg">
                    {/* Header with Logo */}
                    <div className="text-center mb-8">
                        <img 
                            src="/tgosm-logo.png" 
                            alt="TGOSM" 
                            className="w-20 h-20 mx-auto mb-4 object-contain"
                        />
                        <h1 className="text-3xl font-bold text-white mb-2">
                            Shooting Einverständnis
                        </h1>
                        <p className="text-white/60">The Guardians of Social Media</p>
                    </div>

                {/* Progress */}
                <div className="flex gap-1 mb-6">
                    {Array.from({ length: totalSlides }).map((_, i) => (
                        <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                                i <= currentSlide ? 'bg-primary' : 'bg-white/20'
                            }`}
                        />
                    ))}
                </div>
                <div className="text-center text-white/50 text-sm mb-4">
                    {currentSlide + 1} von {totalSlides}
                </div>

                {/* Form Card */}
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 min-h-[400px] relative overflow-hidden">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentSlide}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.3 }}
                            className="space-y-4"
                        >
                            {/* Slide 0: Company Name (Firma) */}
                            {currentSlide === 0 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Für welche Firma ist das Shooting? *</Label>
                                    <ClientPicker
                                        value={formData.company_name}
                                        onChange={(name) => updateField('company_name', name)}
                                        label="Firma aus Liste wählen"
                                        placeholder="Firma suchen..."
                                        fallbackLabel="Firma nicht in der Liste"
                                        fallbackPlaceholder="z.B. Nike, Red Bull, Samsung..."
                                        customLabel="Spontane / externe Firma"
                                        inputClassName="text-lg py-3"
                                    />
                                    <p className="text-white/50 text-sm">
                                        Wähle die Firma aus der Liste. Falls sie noch nicht im System ist, aktiviere oben das Häkchen und gib den Namen manuell ein.
                                    </p>
                                </div>
                            )}

                            {/* Slide 1: Contact Info */}
                            {currentSlide === 1 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Deine Kontaktdaten *</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-white/70 text-sm">Vorname</Label>
                                            <Input
                                                value={formData.first_name}
                                                onChange={(e) => updateField('first_name', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="Max"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-white/70 text-sm">Nachname</Label>
                                            <Input
                                                value={formData.last_name}
                                                onChange={(e) => updateField('last_name', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="Mustermann"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-white/70 text-sm">E-Mail</Label>
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => updateField('email', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white mt-1"
                                            placeholder="max@beispiel.at"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-white/70 text-sm">Telefonnummer</Label>
                                        <Input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => updateField('phone', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white mt-1"
                                            placeholder="+43 660 1234567"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Slide 2: Shooting Date */}
                            {currentSlide === 2 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Wann findet das Shooting statt? *</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-start text-left bg-white/10 border-white/20 text-white hover:bg-white/20 py-6 text-lg"
                                            >
                                                {formData.shooting_date 
                                                    ? format(formData.shooting_date, 'dd. MMMM yyyy', { locale: de })
                                                    : 'Datum wählen...'}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                                            <Calendar
                                                mode="single"
                                                selected={formData.shooting_date}
                                                onSelect={(date) => updateField('shooting_date', date)}
                                                locale={de}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            )}

                            {/* Slide 3: Terms & Conditions */}
                            {currentSlide === 3 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Allgemeine Geschäftsbedingungen</Label>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 max-h-[200px] overflow-y-auto">
                                        <pre className="text-white/70 text-xs whitespace-pre-wrap font-sans leading-relaxed">
                                            {TERMS_TEXT}
                                        </pre>
                                    </div>
                                    <label className="flex items-start gap-3 cursor-pointer p-4 bg-white/5 rounded-xl border border-white/20 hover:bg-white/10 transition-colors">
                                        <Checkbox
                                            checked={formData.consent_accepted}
                                            onCheckedChange={(c) => updateField('consent_accepted', c)}
                                            className="mt-0.5"
                                        />
                                        <span className="text-white text-sm">
                                            Ich habe die Allgemeinen Geschäftsbedingungen gelesen und stimme diesen zu. *
                                        </span>
                                    </label>

                                    {/* Turnstile CAPTCHA */}
                                    {TURNSTILE_SITE_KEY && (
                                        <div className="flex justify-center pt-2">
                                            <Turnstile
                                                siteKey={TURNSTILE_SITE_KEY}
                                                onSuccess={(token) => setTurnstileToken(token)}
                                                onError={() => setTurnstileToken(null)}
                                                onExpire={() => setTurnstileToken(null)}
                                                options={{ theme: 'dark' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Slide 4: Signature */}
                            {currentSlide === 4 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg flex items-center gap-2">
                                        <PenTool className="w-5 h-5" />
                                        Deine Unterschrift *
                                    </Label>
                                    <p className="text-white/50 text-sm">
                                        Bitte unterschreibe mit deinem Finger oder Maus im Feld unten.
                                    </p>
                                    
                                    <div className="bg-white rounded-xl p-2 relative">
                                        <SignatureCanvas
                                            ref={signatureRef}
                                            canvasProps={{
                                                className: 'w-full h-48 rounded-lg',
                                                style: { width: '100%', height: '192px' }
                                            }}
                                            backgroundColor="white"
                                            penColor="black"
                                            onEnd={saveSignature}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={clearSignature}
                                            className="absolute top-4 right-4 bg-white/80 hover:bg-white"
                                        >
                                            <RotateCcw className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    {!formData.signature_data && (
                                        <p className="text-yellow-400/80 text-sm text-center">
                                            Bitte unterschreibe im weißen Feld
                                        </p>
                                    )}
                                    
                                    {/* Summary */}
                                    <div className="p-4 bg-white/5 rounded-xl space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-white/50">Firma:</span>
                                            <span className="text-white">{formData.company_name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-white/50">Name:</span>
                                            <span className="text-white">{formData.first_name} {formData.last_name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-white/50">Datum:</span>
                                            <span className="text-white">
                                                {formData.shooting_date ? format(formData.shooting_date, 'dd.MM.yyyy') : '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Navigation */}
                <div className="flex gap-4 mt-6">
                    {currentSlide > 0 && (
                        <Button
                            variant="outline"
                            onClick={goPrev}
                            className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                        </Button>
                    )}
                    
                    {currentSlide < totalSlides - 1 ? (
                        <Button
                            onClick={goNext}
                            disabled={!canProceed()}
                            className="flex-1"
                            style={{ background: canProceed() ? 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' : undefined }}
                        >
                            Weiter <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            disabled={!canProceed() || isSubmitting}
                            className="flex-1"
                            style={{ background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' }}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Wird gesendet...
                                </>
                            ) : (
                                <>Einverständnis absenden</>
                            )}
                        </Button>
                    )}
                </div>
                </div>
            </div>
            
            {/* Footer */}
            <footer className="py-6 text-center text-white/40 text-sm">
                <p>© {new Date().getFullYear()} The Guardians of Social Media GmbH. Alle Rechte vorbehalten.</p>
            </footer>
        </div>
    );
};

export default ShootingEinverstaendnisPage;
